import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetEmailLogEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { IEmailLog } from '@nxsys/types/models/emailLog';
import { formatStatus, getTaxWeekTaxYear } from '@nxsys/utils';
import { Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ViewEmailLogModal from './components/ViewEmailLogModal';
import ViewEmailSettingModal from './components/ViewEmailSettingModal';

// NX-6289: the email logs that carry a payslip PDF (payslip and draft
// payslip). Other email logs have no file to download.
const PAYSLIP_EMAIL_LOG_ACTIONS = [
  'SEND_SELF_BILL_PAYSLIP_PAYROLL_PROCESS',
  'SEND_SELF_BILL_PAYSLIP_MANUAL',
  'SEND_SELF_BILL_PAYSLIP_DOWNLOAD_BANK_FILE',
  'SEND_DRAFT_SELF_BILL_PAYSLIP_MANUAL',
];

// The file the email was sent with; logs written before it was kept fall back
// to the payslip's / draft payslip's current file.
const getPayslipFileUrl = (record: IEmailLog): string | undefined =>
  PAYSLIP_EMAIL_LOG_ACTIONS.includes(record?.action)
    ? record?.dataSend?.attachmentUrl || record?.selfBill?.s3Url || undefined
    : undefined;

type Props = {
  employeeId: string;
};

const EmailLog: React.FC<Props> = ({ employeeId }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const { emailLogList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = emailLogList;

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxWeekTaxYear.taxYear,
    });
  }, []);

  const handleFetchData = (params: GetEmailLogEmployeeListParams) => {
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getEmailLogEmployeeList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IEmailLog | null>(null);

  const toggleViewModal = (record?: IEmailLog) => {
    setOpenViewModal(!openViewModal);
    setSelectedData(record || null);
  };

  const [openViewSettingModal, setOpenViewSettingModal] = useState(false);
  const toggleViewSettingModal = (record?: IEmailLog) => {
    setOpenViewSettingModal(!openViewSettingModal);
    setSelectedData(record || null);
  };

  const columns: ColumnsType<IEmailLog> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => toggleViewModal(record)}>
            <Icons.IconEye />
          </li>
          <li onClick={() => toggleViewSettingModal(record)}>
            <Icons.IconEmailSetting />
          </li>
          {getPayslipFileUrl(record) && (
            <Tooltip title={messages['common.download']}>
              <li
                className="cursor-pointer"
                onClick={() => window.open(getPayslipFileUrl(record), '_blank')}
              >
                <Icons.IconDownload />
              </li>
            </Tooltip>
          )}
        </ul>
      ),
      width: 120,
    },
    {
      key: 'status',
      title: messages['common.status'],
      width: 120,
      dataIndex: 'status',
      render: (value) => (
        <span className={`${formatStatus(value)}`}>
          <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
            {formatStatus(value)}
          </AppTagStatus>
        </span>
      ),
    },
    {
      key: 'emailTo',
      title: messages['common.emailTo'],
      width: 250,
      render: (record) =>
        record?.dataSend?.mailTo ||
        record?.selfBill?.subcontractorRef?.email ||
        '-',
    },
    {
      key: 'subject',
      title: messages['common.emailSubject'],
      dataIndex: 'dataSend',
      width: 250,
      render: (value) => value?.subject || '-',
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      dataIndex: 'selfBill',
      width: 200,
      render: (value) => `${value?.taxPeriod || ''}/${value?.taxYear || ''}`,
    },
    {
      key: 'payrollId',
      title: messages['common.payrollId'],
      dataIndex: 'selfBill',
      width: 150,
      render: (value) => value?.payrollRef?.id || '_',
    },
    {
      key: 'payslipId',
      title: messages['common.selfBillPayslipId'],
      dataIndex: 'selfBill',
      width: 200,
      render: (value, record) => {
        const { id, payrollRef } = value;
        const displayId = [
          PAYMENT_MODEL_ENUM.CIS,
          PAYMENT_MODEL_ENUM.NON_CIS,
        ].includes(payrollRef?.paymentModel)
          ? id
          : id?.replace('SFB', 'PAY');
        if (!displayId) return '-';
        return (
          <>
            {displayId}
            {record?.isDraft && (
              <Tag color="warning" className="ml-1">
                {messages['common.draft']}
              </Tag>
            )}
          </>
        );
      },
    },
    {
      key: 'companyPaymentId',
      title: messages['common.employerId'],
      dataIndex: 'selfBill',
      width: 200,
      render: (value) => value?.payrollRef?.companyRef?.id || '-',
    },
    {
      key: 'agencyId',
      title: messages['common.agencyId'],
      dataIndex: 'selfBill',
      width: 200,
      render: (value) => value?.payrollRef?.agencyRef?.id || '_',
    },
    {
      key: 'createdAt',
      title: messages['common.lastSent'],
      dataIndex: 'createdAt',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value
          ? moment(value).tz('Europe/London').format('DD/MM/YYYY @ hh.mm a')
          : '-',
    },
    {
      key: 'sentBy',
      title: messages['common.sentBy'],
      width: 200,
      render: (record) => {
        const { sentBy, isSuperAdmin } = record;
        return isSuperAdmin
          ? 'Super Admin'
          : `${sentBy?.forename || ''} ${sentBy?.surname || ''}`;
      },
    },
    {
      key: 'errorMessage',
      title: messages['common.logDetails'],
      dataIndex: 'errorMessage',
      width: 350,
      render: (value) => value || '-',
    },
  ];

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.emailLog']}
      </Typography.Title>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
        <div className="flex items-center gap-x-2">
          <TaxYearSelect
            initValue={currentTaxWeekTaxYear.taxYear}
            onFilter={(value) =>
              handleFetchData({ ...queryParams, taxYear: value.taxYear })
            }
          />
          <TaxWeekMonthSelect
            onFilter={(value) =>
              handleFetchData({ ...queryParams, taxPeriod: value.taxPeriod })
            }
          />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 3000, y: 700 }}
          titleEmpty={messages['emailLog.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
      {selectedData && (
        <>
          <ViewEmailLogModal
            isOpen={openViewModal}
            toggle={toggleViewModal}
            data={selectedData}
          />
          <ViewEmailSettingModal
            isOpen={openViewSettingModal}
            toggle={toggleViewSettingModal}
            data={selectedData}
          />
        </>
      )}
    </div>
  );
};

export default EmailLog;
